import deepSortObject from 'deep-sort-object';

/** Returns the first value of an object, or undefined if the object is empty or not provided */
export const firstValueOf = <T extends Record<string, unknown>>(
  obj?: string | T
) => {
  if (obj && typeof obj === 'object') {
    return Object.values(obj)[0] as T[keyof T];
  }
  return obj;
};

export const sortObjectKeysDeep = deepSortObject;

export const sortObjectKeys = (obj: any) =>
  Object.fromEntries(Object.entries(obj).sort());

/**
 * Recursively removes empty values from an object or array.
 *
 * Empty values include:
 * - `null`
 * - `undefined`
 * - empty strings (`''`)
 * - empty arrays (`[]`)
 * - empty objects (`{}`)
 *
 * You can also optionally skip specific keys from cleaning (e.g., metadata fields).
 *
 * @param input - The input object or array to clean.
 * @param skipKeys - Optional array of keys to ignore during cleaning.
 * @returns A cleaned version of the input with all empty values removed, or `undefined` if fully empty.
 */
export const deepClean = (
  input: unknown,
  skipKeys: string[] = []
): unknown => {
  if (Array.isArray(input)) {
    const cleanedArray = input
      .map(item => deepClean(item, skipKeys))
      .filter(item => !isEmptyValue(item));
    return cleanedArray.length > 0 ? cleanedArray : undefined;
  }

  if (isPlainObject(input)) {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(input)) {
      if (skipKeys.includes(key)) continue;
      const cleanedValue = deepClean(value, skipKeys);
      if (!isEmptyValue(cleanedValue)) {
        result[key] = cleanedValue;
      }
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  return isEmptyValue(input) ? undefined : input;
};

/**
 * Determines if a value is a plain JavaScript object.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  value.constructor === Object;

/**
 * Checks whether a value is considered "empty" for the purpose of cleaning.
 */
const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (isPlainObject(value) && Object.keys(value).length === 0);
